import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Pagination, PaginationDropdown, Search, Table,
} from '@mesa-labs/mesa-ui';
import { ColumnProp } from '@mesa-labs/mesa-ui/dist/components/Table';

import { DefaultPaginationDropdownLimits } from '@mesa-labs/mesa-ui/dist/components/Pagination';
import { useGetAllExternalClientsAsFacilitatorQuery } from '../../redux/api/facilitators';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateLimit, updatePage, updateSearchTerm, updateTotalPages, updateSortField, updateSortDirection,
} from '../../redux/slices/clients';
import { FilterControls, FilterSection } from '../common/Filters';
import { PaginationContainer } from '../common/Pagination';
import { Header } from '../common/Header';
import { DashboardEvents, trackEvent } from '../../analytics';

const ClientsPageContainer = styled.div`
  background: ${(props) => props.theme.colors.White};
  border: 1px solid #EBEAED;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PageControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const SearchContainer = styled.div`
  width: 600px;
`;

function ClientsPage(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const partnerId = useSelector((state) => state.auth.partnerId);
  const searchTerm = useSelector((state) => state.clients.searchTerm);
  const page = useSelector((state) => state.clients.page) || 1;
  const totalPages = useSelector((state) => state.clients.totalPages);
  const limit = useSelector((state) => state.clients.limit) || DefaultPaginationDropdownLimits[0];
  const sortField = useSelector((state) => state.clients.sortField);
  const sortDirection = useSelector((state) => state.clients.sortDirection);

  const [searchInput, setSearchInput] = useState(searchTerm || '');

  const filter = useMemo(() => ({
    search: searchTerm || '',
    sortField,
    sortDirection,
    limit: (typeof limit === 'number' ? limit : limit.value),
    page,
  }), [searchTerm, sortField, sortDirection, limit, page]);

  const {
    data: {
      data: clients = [],
      total: totalClients,
    } = {},
    isFetching,
  } = useGetAllExternalClientsAsFacilitatorQuery({
    accessToken: accessToken!,
    partnerId: partnerId!,
    ...filter,
  }, {
    skip: !partnerId
      // force a searchTerm for JLL facilitators
      || (partnerId === api.Partners.JLL && !searchTerm)
  });

  const rows = useMemo(() => clients.map((client) => ({
    id: `${client.partnerId}:${client.externalClientId}`,
    externalClientId: client.externalClientId,
    ...(partnerId !== api.Partners.JLL && {
      externalClientName: client.externalClientName,
    }),
    totalInternalVendors: client.totalInternalVendors,
    totalServicedVendorsT12m: client.totalServicedVendorsT12m,
    totalServicedInvoiceSpendT12m: client.totalServicedInvoiceSpendT12m,
    currency: 'USD',
  })), [clients]);

  useEffect(() => {
    if (totalClients !== undefined) {
      dispatch(updateTotalPages(Math.ceil(totalClients / (typeof limit === 'number' ? limit : limit.value))));
    }
  }, [limit, totalClients]);

  const onSubmitSearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      dispatch(updateSearchTerm(trimmed));
    } else {
      setSearchInput('');
      dispatch(updateSearchTerm(''));
    }
    dispatch(updatePage(1));
  };

  const clientColumns: ColumnProp[] = useMemo(() => ([
    {
      key: 'externalClientId',
      label: 'Company No.',
      sortField: 'externalClientId',
      width: partnerId !== api.Partners.JLL ? '15%' : '25%',
    },
    ...(partnerId !== api.Partners.JLL && [{
      key: 'externalClientName',
      label: 'Client Name',
      sortField: 'externalClientName',
      width: '30%',
    }] || []),
    {
      key: 'totalInternalVendors',
      label: 'Total Suppliers',
      sortField: 'totalInternalVendors',
      width: partnerId !== api.Partners.JLL ? '15%' : '25%',
    },
    {
      key: 'totalServicedVendorsT12m',
      label: 'Total Funded Suppliers',
      sortField: 'totalServicedVendorsT12m',
      tooltip: 'Total number of suppliers enabled for Mesa with funded invoices in the past 12 months.',
      width: partnerId !== api.Partners.JLL ? '20%' : '25%',
    },
    {
      key: 'totalServicedInvoiceSpendT12m',
      label: 'Total Funded Spend',
      sortField: 'totalServicedInvoiceSpendT12m',
      type: 'currency',
      tooltip: 'Total funded invoice spend in the trailing 12 months.',
      width: partnerId !== api.Partners.JLL ? '20%' : '25%',
    },
  ]), [partnerId]);

  useEffect(() => {
    trackEvent(DashboardEvents.SourcingClientsView);
  }, []);

  return (
    <>
      <Header>Client Search</Header>
      <ClientsPageContainer>

        <PageControlsContainer>
          <FilterSection
            width="100%"
            filterControl={(
              <FilterControls>
                <SearchContainer>
                  <Search
                    placeholder="Search by Company Number. Press Enter to search, Escape to clear"
                    onChange={(value) => setSearchInput(value)}
                    onSubmit={onSubmitSearch}
                    value={searchInput}
                    expanded
                  />
                </SearchContainer>
              </FilterControls>
            )}
          >
          </FilterSection>
        </PageControlsContainer>

        {((partnerId === api.Partners.JLL && !!searchTerm) || true) && (
          <Table
            isLoading={isFetching}
            columns={clientColumns}
            rows={rows}
            currentSortColumn={sortField}
            currentSortDirection={sortDirection}
            setSortField={(field: string) => dispatch(updateSortField(field))}
            setSortDirection={(direction: api.SortDirection) => dispatch(updateSortDirection(direction))}
            onRowClick={(row: Record<string, unknown>) => navigate(`/clients/${row.externalClientId}`)}
            paginationComponent={(
              <PaginationContainer>
                <PaginationDropdown
                  selectedLimit={(typeof limit === 'number' ? DefaultPaginationDropdownLimits.find((l) => l.value === limit) || DefaultPaginationDropdownLimits[0] : limit)}
                  onChange={(item) => {
                    dispatch(updateLimit(item));
                    dispatch(updatePage(1));
                  }}
                />

                <Pagination
                  currentPage={page}
                  totalPages={totalPages || 0}
                  onPrev={() => dispatch(updatePage(page - 1))}
                  onNext={() => dispatch(updatePage(page + 1))}
                />
              </PaginationContainer>
            )}
            customStyles={{
              columnsPadding: '24px',
              columnsBorderRadius: 0,
              paginationPadding: '0px',
              rowPadding: '0px 24px',
              columnColor: '#717680',
              columnFontSize: '12px',
              columnFontWeight: 600,
            }}
          />
        )}
      </ClientsPageContainer>
    </>
  );
}

export default ClientsPage;
